import { inject, Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { DeviceLoginApprovedEvent } from '@auth/data-access';

export type DeviceLoginHubStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

class AggressiveReconnectPolicy implements signalR.IRetryPolicy {
  private readonly maxDelayMs = 60000;
  private readonly baseDelayMs = 500;

  public nextRetryDelayInMilliseconds(
    retryContext: signalR.RetryContext,
  ): number {
    if (retryContext.previousRetryCount === 0) return 0;
    const attempt = retryContext.previousRetryCount - 1;
    const delay = this.baseDelayMs * Math.pow(2, attempt);
    return Math.min(delay, this.maxDelayMs);
  }
}

@Injectable({ providedIn: 'root' })
export class DeviceLoginRealtimeService implements OnDestroy {
  private readonly appConfig = inject(APP_CONFIG);
  private readonly apiBase = this.appConfig.apiUrl.userService;

  private connection: HubConnection | null = null;
  private isStarting = false;

  private readonly hubStatusSubject = new BehaviorSubject<DeviceLoginHubStatus>(
    'disconnected',
  );
  public readonly hubStatus$: Observable<DeviceLoginHubStatus> =
    this.hubStatusSubject.asObservable();

  private readonly approvedSubject = new Subject<DeviceLoginApprovedEvent>();
  public readonly approved$ = this.approvedSubject.asObservable();

  public async start(): Promise<void> {
    if (
      this.isStarting ||
      this.connection?.state === HubConnectionState.Connected ||
      this.connection?.state === HubConnectionState.Connecting
    ) {
      return;
    }

    this.isStarting = true;
    this.emitStatus('connecting');

    try {
      if (this.connection) {
        await this.stop();
      }

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.apiBase}/hubs/device-login`, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect(new AggressiveReconnectPolicy())
        .build();

      this.setupHandlers();

      await this.connection.start();
      this.emitStatus('connected');
    } catch (error) {
      this.emitStatus('disconnected');
      throw error;
    } finally {
      this.isStarting = false;
    }
  }

  public async stop(): Promise<void> {
    this.emitStatus('disconnected');
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch {
        // ignore
      }
      this.connection = null;
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  public async joinDeviceLogin(deviceCode: string): Promise<void> {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    await this.connection.invoke('JoinDeviceLogin', deviceCode);
  }

  private setupHandlers(): void {
    if (!this.connection) return;

    /**
     * Event name varies by backend config (camelCase per spec, but some ASP.NET
     * setups emit PascalCase). Listen to both — whichever the server sends, we
     * promote the payload to the typed Subject.
     */
    const candidates = [
      'deviceLoginApproved',
      'DeviceLoginApproved',
      'OnDeviceLoginApproved',
    ];
    for (const name of candidates) {
      this.connection.off(name);
      this.connection.on(name, (payload: unknown) => {
        if (!payload || typeof payload !== 'object') return;
        this.approvedSubject.next(payload as DeviceLoginApprovedEvent);
      });
    }

    this.connection.onclose(() => {
      this.emitStatus('disconnected');
    });
    this.connection.onreconnecting(() => {
      this.emitStatus('reconnecting');
    });
    this.connection.onreconnected(() => {
      this.emitStatus('connected');
    });
  }

  private emitStatus(status: DeviceLoginHubStatus): void {
    this.hubStatusSubject.next(status);
  }

  ngOnDestroy(): void {
    this.stop();
    this.hubStatusSubject.complete();
    this.approvedSubject.complete();
  }
}
